/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import {
  CONTENT_TYPE_JSON,
  TOKEN_EXPIRED_MESSAGE,
  TOKEN_EXPIRED_OR_INVALID_MESSAGE,
} from "../../utils/apiConstants.js";
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview Events List logic module.
 * Handles event list operations including fetching and filtering
 * registration events and business events for display.
 * @module logic/eventsList
 */

import { refreshToken, signOut } from "../../utils/common.js";

const NON_MEMBER_USER_TYPE = "4";
const MAX_LISTED_EVENTS = 3;

const membershipAvailabilityDate = (event, isNonMember) =>
  isNonMember ? event.fechaDisponibleNoSocios : event.fechaDisponibleSocios;

const isEventOpenForUser = (event, isNonMember, currentDate) => {
  const availableOn = membershipAvailabilityDate(event, isNonMember);
  if (!availableOn) {
    return true;
  }
  return currentDate >= new Date(availableOn);
};

const isUpcomingEvent = (event, dateField, isNonMember, currentDate) => {
  if (new Date(event[dateField]) < currentDate) {
    return false;
  }
  return isEventOpenForUser(event, isNonMember, currentDate);
};

const compareByDateField = (primaryField, fallbackField) => (left, right) => {
  const leftDate = new Date(
    fallbackField ? left[primaryField] || left[fallbackField] : left[primaryField],
  );
  const rightDate = new Date(
    fallbackField
      ? right[primaryField] || right[fallbackField]
      : right[primaryField],
  );
  return leftDate - rightDate;
};

const sortEventsByDate = (events, primaryField, fallbackField) =>
  [...events].sort(compareByDateField(primaryField, fallbackField));

/**
 * Fetches and sets user role/permission data.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Function} changePermissions - Callback to update permissions state
 * @returns {Promise<void>}
 */
export const getUserRoleData = async (userId, navigate, changePermissions) => {
  const tokenToUse = localStorage.getItem("token");
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${tokenToUse}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}permission/${userId}`,
      options,
    );
    const roleData = await response.json();
    const isTokenExpired =
      roleData?.message === TOKEN_EXPIRED_MESSAGE ||
      roleData?.message === TOKEN_EXPIRED_OR_INVALID_MESSAGE;
    if (isTokenExpired) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return;
      }
      await getUserRoleData(userId, navigate, changePermissions);
      return;
    }
    if (response.ok && roleData?.permisos != null) {
      changePermissions(roleData.permisos);
    }
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
  }
};

/**
 * Fetches registration events available to a user based on user type and date filters.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @param {string} userType - User type identifier (e.g., "4" for non-members)
 * @returns {Promise<Array>} Sorted array of available registration events
 */
export const getEvents = async (userId, navigate, userType) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}event/user/${userId}`,
      options,
    );
    const data = await response.json();
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getEvents(userId, navigate, userType);
    }
    if (Array.isArray(data)) {
      const currentDate = new Date();
      const availableEvents = data.filter((event) =>
        isUpcomingEvent(
          event,
          "fechaEvento",
          userType === NON_MEMBER_USER_TYPE,
          currentDate,
        ),
      );
      return sortEventsByDate(availableEvents, "fechaEvento");
    }
    return [];
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches business events available to a user based on membership type and date filters.
 * @async
 * @param {string} userId - User identifier
 * @param {Object} user - User object with tipoDeUsuario property
 * @param {Function} navigate - React Router navigation function
 * @returns {Promise<Array>} Sorted array of available business events
 */
export const getBusinessEvents = async (userId, user, navigate) => {
  const options = {
    method: "GET",
    headers: {
      "Content-Type": CONTENT_TYPE_JSON,
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  try {
    const response = await fetch(
      `${process.env.REACT_APP_ROOT_API}businessEvent/user/${userId}`,
      options,
    );
    const data = await response.json();
    logger.info("getBusinessEvents -> data", { data: data });
    if (data?.message === TOKEN_EXPIRED_MESSAGE) {
      const newTokens = await refreshToken();
      if (!newTokens) {
        await signOut();
        navigate("/");
        return [];
      }
      return await getBusinessEvents(userId, user, navigate);
    }
    if (Array.isArray(data)) {
      const currentDate = new Date();
      const activeEvents = data.filter((event) => event.activo === true);
      const availableEvents = activeEvents.filter((event) =>
        isUpcomingEvent(
          event,
          "fechaFinEvento",
          user?.tipoDeUsuario === NON_MEMBER_USER_TYPE,
          currentDate,
        ),
      );
      return sortEventsByDate(availableEvents, "fechaInicioEvento");
    }
    return [];
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};

/**
 * Fetches and merges all events (registration + business), returns first 3 sorted by date.
 * @async
 * @param {string} userId - User identifier
 * @param {Function} navigate - React Router navigation function
 * @param {Object} user - User object with tipoDeUsuario property
 * @returns {Promise<Array>} First 3 events sorted by date
 */
export const getAllEvents = async (userId, navigate, user) => {
  try {
    const registrationEvents = await getEvents(
      userId,
      navigate,
      user?.tipoDeUsuario,
    );
    const businessEvents = await getBusinessEvents(userId, user, navigate);

    // Merge both arrays
    const allEvents = [
      ...(registrationEvents || []),
      ...(businessEvents || []),
    ];

    const sortedEvents = sortEventsByDate(
      allEvents,
      "fechaEvento",
      "fechaInicioEvento",
    );
    return sortedEvents.slice(0, MAX_LISTED_EVENTS);
  } catch (error) {
    logger.error("Error", { err: sanitizeError(error) });
    return [];
  }
};
